import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Dwelling } from '../../model/dwelling';
import { Parking } from '../../model/parking';
import { Premises } from '../../model/premises';
import { OtherRentalObject } from '../../model/other-rental-object';
import { RentalObjectKey } from '../../model/rental-object-key';
import { KeyTypeService } from '../../service/data/key-type.service';

@Component({
    selector: 'app-rental-object-keys',
    templateUrl: './rental-object-keys.component.html',
    styleUrls: ['./rental-object-keys.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentalObjectKeysComponent implements OnInit, OnDestroy {
  constructor(
    private _keyTypeService: KeyTypeService,
    private _fb: FormBuilder,
    private _cf: FormControlExFactory
  ) {}

  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() keys: RentalObjectKey[];
  @Input() rentalObject: Dwelling | Premises | Parking | OtherRentalObject;
  private _subscription = new Subscription();

  keyTypes: SelectionItem[] = [];

  addKey() {
    this.forms.controls.push(
      this.createForm(
        new RentalObjectKey({
          quantity: 1,
          quantityLentOut: 0,
          keyTypeId: this.keyTypes.length > 0 ? this.keyTypes[0].value : null,
        })
      )
    );
    this.forms.markAsDirty();
  }

  delete(keyForm: FormGroup) {
    this.forms.controls.splice(this.forms.controls.indexOf(keyForm), 1);
    this.forms.markAsDirty();
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  initForm() {
    let keys = this.rentalObject ? this.rentalObject.keys : this.keys;
    for (let key of keys) {
      let group = this.createForm(key);
      this.forms.controls.push(group);
    }
  }

  private createForm(key: RentalObjectKey): FormGroup {
    let group = this._fb.group({
      id: this._cf.createHidden(key.id),
      keyTypeId: this._cf.createMandatory('KEYTYPE.KEYTYPE', key.keyTypeId),
      quantity: this._cf.createWithOptions('RENTALOBJECT_KEY.QUANTITY', key.quantity, { minValue: 0 }),
      quantityLentOut: this._cf.createWithOptions('RENTALOBJECT_KEY.QUANTITY_LENT_OUT', key.quantityLentOut, {
        required: true,
        validator: Validators.compose([Validators.max(key.quantity), Validators.min(0)]),
      }),
      quantityInStorage: this._cf.createReadonly('RENTALOBJECT_KEY.QUANTITY_IN_STORAGE', key.quantity - key.quantityLentOut),
      systemNumber: this._cf.createMaxLength('RENTALOBJECT_KEY.SYSTEM_NUMBER', key.systemNumber, 100),
      marking: this._cf.createMaxLength('RENTALOBJECT_KEY.MARKING', key.marking, 100),
      caption: this._cf.createMaxLength('RENTALOBJECT_KEY.CAPTION', key.caption, 100),
      key: this._cf.createMaxLength('RENTALOBJECT_KEY.KEY', key.key, 100),
      supplier: this._cf.createMaxLength('RENTALOBJECT_KEY.SUPPLIER', key.supplier, 100),
    });

    this._subscription.add(group.controls['keyTypeId'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(
      group.controls['quantity'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
        this.setLentOutValidator(group);
        this.updateStorage(group);
      })
    );
    this._subscription.add(
      group.controls['quantityLentOut'].valueChanges.subscribe(val => {
        this.forms.markAsDirty();
        this.updateStorage(group);
      })
    );
    this._subscription.add(group.controls['systemNumber'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['marking'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['caption'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['supplier'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    this._subscription.add(group.controls['key'].valueChanges.subscribe(val => this.forms.markAsDirty()));
    return group;
  }

  setLentOutValidator(group: FormGroup) {
    let quantity = group.controls['quantity'].value;
    group.controls['quantityLentOut'].setValidators([Validators.min(0), Validators.max(quantity)]);
    group.controls['quantityLentOut'].markAllAsTouched();
  }

  ngOnInit(): void {
    this.initForm();
    this._subscription.add(
      this._keyTypeService.getList().subscribe(list => {
        this.keyTypes = list
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(t => new SelectionItem(t.name, t.id));
      })
    );
  }

  updateStorage(group: FormGroup) {
    let quantity = group.controls['quantity'].value;
    let lentOut = group.controls['quantityLentOut'].value;
    group.controls['quantityInStorage'].setValue(lentOut > quantity ? 0 : quantity - lentOut);
  }
}
